import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  dateFormat,
  splitMissionName,
} from "../../../services/Utils/functionService";
import ProgressBar from "../../../components/Cac/Partner/Utils/MonitoringElements/ProgressBar";
import ModalDocRequestDetails from "../../../components/Cac/Partner/Request/ModalDocRequestDetails";
import Return from "../../../assets/icons/return.svg";
import StatusRequest from "../../../components/Cac/Customer/Request/StatusRequest";
import useModal from "../../../hooks/useModal";
import { useTokenContext } from "../../../context/tokenContext";
import { usePartnerRequestsStore2 } from "../../../stores/Cac/Partner/partnerRequests";
import ModalCreateRequest from "../../../components/Cac/Partner/Request/ModalCreateRequest";
import { useNotifsStore } from "../../../stores/Utils/notifs";
import {
  getDocRequests,
  getMissionDataByCustomer,
  handleExportCSV,
  handleExportTemplate,
  importService,
} from "../../../services/Api/customerService";
import {
  showToastError,
  showToastSuccess,
} from "../../../services/Toasts/toasts";
import { usePartnerMissionStore } from "../../../stores/Cac/Partner/partnerMission";
import SideBar from "../../../components/Layout/SideBar";
import Header from "../../../components/Layout/Header";
import Footer from "../../../components/Layout/Footer";
import SearchBar from "../../../components/Admin/Services/SearchBar";
import { useServiceStore } from "../../../stores/admin/service";
import { PartnerRequestTable } from "../../../components/Cac/Partner/Request/PartnerRequestTable";

const PartnerRequests = () => {
  const { t } = useTranslation();
  // Hooks
  const { modalContent, isModalOpen, openModal, closeModal } = useModal();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { idPartner, idCustomer, idMission } = useParams();
  const { roleId } = useTokenContext();
  const { notifs } = useNotifsStore();
  const { renewable } = usePartnerMissionStore();
  const {
    requests,
    setRequests,
    missionData,
    setMissionData,
    setRequestIds,
    choiceModal,
    setChoiceModal,
    editStatus,
    setEditStatus,
    updateRequest,
    setUpdateRequest,
    openDetails,
    setOpenDetails,
    setRequestId,
    displayAllRequests,
    setDisplayAllRequests,
    affectedUsers,
    setAffectedUsers,
    dataRequests,
    setDataRequests,
  } = usePartnerRequestsStore2();
  const { searchTerm } = useServiceStore();
  // States
  const [loadingImport, setLoadingImport] = useState<boolean>(false);

  // Function for get the data of the mission
  useEffect(() => {
    if (idMission) {
      getMissionDataByCustomer(Number(idCustomer), Number(idMission))
        .then((data) => setMissionData(data))
        .catch(() => showToastError(t("common.error")));
    }
  }, [idMission]);

  useEffect(() => {
    if (idCustomer && idMission)
      getDocRequests(parseInt(idCustomer), parseInt(idMission))
        .then((data) => setDataRequests(data))
        .catch(() => showToastError(t("common.error")));
  }, [idMission, updateRequest, notifs]);

  useEffect(() => {
    if (openDetails === undefined || openDetails === false) {
      setRequestId(0);
    } else {
      setRequestIds([]);
      setEditStatus(false);
    }
  }, [openDetails]);

  // Function for import CSV
  const handleUpload = async () => {
    if (
      fileInputRef.current &&
      fileInputRef.current.files &&
      fileInputRef.current.files.length > 0
    ) {
      const originalFile = fileInputRef.current.files[0];
      try {
        setLoadingImport(true);
        const importResult = await importService(
          Number(idCustomer),
          Number(idMission),
          originalFile,
        );

        if (importResult === "success") {
          showToastSuccess(t("partner.importSuccess"));
          setUpdateRequest(!updateRequest);
          setLoadingImport(false);
        }
      } catch (error) {
        showToastError(t("partner.importError"));
        setLoadingImport(false);
      }
    }
  };

  // Gère l'affectation multiple
  useEffect(() => {
    if (affectedUsers) {
      setEditStatus(false);
    } else if (editStatus) {
      setAffectedUsers(false);
    }
  }, [affectedUsers, editStatus]);

  // Gère la recherche
  useEffect(() => {
    const filteredClients = searchTerm
      ? dataRequests.filter(
          (client: any) =>
            client.CategoryDocRequest?.name
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            client.description.toLowerCase().includes(searchTerm.toLowerCase()),
        )
      : dataRequests;
    setRequests(filteredClients);
  }, [searchTerm, dataRequests]);

  return (
    <div className="flex h-dvh w-full flex-col overflow-hidden bg-gradient-to-br from-slate-50 to-blue-50">
      {isModalOpen ? modalContent : null}
      <Header />
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <SideBar />
        <ModalDocRequestDetails openModal={openModal} closeModal={closeModal} />
        <main className="flex min-h-0 flex-1 scroll-pb-24 flex-col overflow-y-auto pb-24 pt-3 sm:scroll-pb-20 sm:pl-14 md:pb-20">
          <div className="mx-auto flex min-h-0 w-full max-w-7xl flex-1 flex-col gap-3 px-3 lg:px-4 2xl:max-w-[2400px]">
            {choiceModal === "create" ? <ModalCreateRequest /> : null}

            {/* Header Section avec design moderne */}
            <section className="w-full flex-shrink-0 rounded-2xl border border-gray-100 bg-white p-4 shadow-md sm:p-5">
              <div className="flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
                <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                  <div className="hidden md:flex flex-shrink-0 rounded-xl bg-yellow-trio p-2.5 sm:p-3">
                    <svg
                      className="h-5 w-5 text-grey-trio sm:h-6 sm:w-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h1 className="font-K2D text-lg font-bold text-gray-700 sm:text-xl">
                      {t("partner.requestsTitle")} :{" "}
                      <span className="text-yellow-trio">
                        {missionData?.name &&
                          splitMissionName(missionData.name)}
                      </span>{" "}
                      {missionData?.closed === 1 ? (
                        <span className="text-red-500">
                          {" "}
                          {t("partner.archived")}
                        </span>
                      ) : (
                        ""
                      )}
                    </h1>
                    <p className="mt-1 text-sm text-gray-600 sm:text-base">
                      {t("customerRequests.dueDate")} :{" "}
                      {dateFormat(missionData?.dueDate)}
                    </p>
                  </div>
                </div>

                <div className="grid w-full grid-cols-1 gap-2 min-[420px]:grid-cols-2 lg:flex lg:w-auto lg:flex-wrap lg:justify-end">
                  {roleId === 1 || roleId === 3 ? (
                    <button
                      type="button"
                      className="hidden md:flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-xl bg-dark-lavender px-4 py-2 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:shadow-lg"
                      onClick={() =>
                        navigate(
                          `/partner/${idPartner}/customers/${idCustomer}/missions/${idMission}/documents`,
                        )
                      }
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      {t("customerRequests.documents")}
                    </button>
                  ) : null}
                  {roleId === 1 || roleId === 3 ? (
                    <button
                      type="button"
                      className="hidden md:flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-xl bg-dark-lavender px-4 py-2 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:shadow-lg"
                      onClick={() =>
                        navigate(
                          `/partner/${idPartner}/customers/${idCustomer}/missions/${idMission}/monitoring`,
                        )
                      }
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                        />
                      </svg>
                      {t("partner.monitoring")}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    disabled={missionData?.closed === 1}
                    className="hidden md:flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-xl bg-dark-lavender px-4 py-2 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={() =>
                      navigate(
                        `/partner/${idPartner}/customers/${idCustomer}/missions/${idMission}/recoveryfile`,
                      )
                    }
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    {t("partner.relaunch")}
                  </button>
                  <button
                    type="button"
                    disabled={missionData?.closed === 1}
                    className="flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-xl bg-yellow-trio px-5 py-2 text-sm font-semibold text-gray-700 shadow-md transition-all duration-200 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={() => setChoiceModal("create")}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    {t("partner.addRequest")}
                  </button>
                </div>
              </div>
            </section>

            {/* Navigation et contrôles */}
            <section className="w-full hidden md:flex flex-shrink-0 rounded-2xl border border-gray-100 bg-white p-3 shadow-md">
              <div className="flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
                <div className="w-full lg:max-w-lg">
                  {requests.length > 0 && (
                    <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center">
                      <span className="flex-shrink-0 text-sm text-gray-600">
                        {t("customerRequests.progress")}
                      </span>
                      <ProgressBar />
                    </div>
                  )}
                </div>
              </div>
              <div className="w-full ">
                <StatusRequest />
              </div>
            </section>

            {/* Barre d'outils */}
            <section className="w-full hidden md:flex flex-shrink-0 rounded-2xl border border-gray-100 bg-white p-3 shadow-md sm:p-4">
              <div className="flex w-full flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
                <div className="flex w-full items-center gap-3 lg:w-auto">
                  <button
                    type="button"
                    className="p-3 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors duration-200 group cursor-pointer"
                    onClick={() => {
                      setOpenDetails(false);
                      setRequestId(0);
                      navigate(
                        `/partner/${idPartner}/customers/${idCustomer}/missions`,
                      );
                    }}
                    title={t("common.back")}
                  >
                    <img
                      src={Return}
                      alt="return"
                      className="w-5 h-5 group-hover:scale-110 transition-transform duration-200"
                    />
                  </button>
                  <div className="min-w-0 flex-1 lg:w-72 lg:flex-none">
                    <SearchBar />
                  </div>
                </div>

                <div className="flex w-full items-center gap-2 overflow-x-auto pb-1 lg:w-auto lg:flex-wrap lg:justify-end lg:overflow-visible lg:pb-0">
                  {/* Import/Export */}
                  <input
                    type="file"
                    id="fileInput"
                    ref={fileInputRef}
                    onChange={handleUpload}
                    onClick={(
                      event: React.MouseEvent<HTMLInputElement, MouseEvent>,
                    ) => {
                      if (event.currentTarget) {
                        event.currentTarget.value = "";
                        event.currentTarget.click();
                      }
                    }}
                    style={{ display: "none" }}
                  />
                  {renewable ? null : (
                    <div className="flex flex-shrink-0 items-center gap-2">
                      <button
                        className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 flex items-center gap-2 cursor-pointer"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={missionData?.closed === 1}
                      >
                        {!loadingImport ? (
                          <>
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                              />
                            </svg>
                            {t("partner.importModel")}
                          </>
                        ) : (
                          <svg
                            aria-hidden="true"
                            role="status"
                            className="inline w-4 h-4 animate-spin text-gray-200"
                            viewBox="0 0 100 101"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                              fill="currentColor"
                            />
                            <path
                              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                              fill="#1C64F2"
                            />
                          </svg>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          handleExportCSV(Number(idCustomer), Number(idMission))
                        }
                        className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 flex items-center gap-2 cursor-pointer"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        {t("partner.exportModel")}
                      </button>
                      <button
                        type="button"
                        disabled={missionData?.closed === 1}
                        onClick={() =>
                          handleExportTemplate(
                            Number(idCustomer),
                            Number(idMission),
                          )
                        }
                        className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 flex items-center gap-2 cursor-pointer"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        {t("partner.template")}
                      </button>
                    </div>
                  )}

                  {/* Boutons d'action */}
                  {renewable && (
                    <button
                      type="button"
                      className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 cursor-pointer"
                      onClick={() => setDisplayAllRequests(!displayAllRequests)}
                    >
                      {displayAllRequests
                        ? t("customerRequests.hideOld")
                        : t("customerRequests.showOld")}
                    </button>
                  )}

                  {!affectedUsers && !editStatus ? (
                    <button
                      type="button"
                      onClick={() => setAffectedUsers(!affectedUsers)}
                      disabled={missionData?.closed === 1}
                      className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors duration-200 flex items-center gap-2 cursor-pointer"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                        />
                      </svg>
                      {t("partner.multiAssign")}
                    </button>
                  ) : null}

                  {!editStatus && !affectedUsers ? (
                    <button
                      onClick={() => setEditStatus(true)}
                      disabled={missionData?.closed === 1}
                      className="px-3 py-2 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors duration-200 flex items-center gap-2 cursor-pointer"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      {t("partner.editStatuses")}
                    </button>
                  ) : null}
                </div>
              </div>
            </section>

            {/* Table des requêtes avec design moderne */}
            <section className="min-h-[24rem] w-full flex-1 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-md">
              <PartnerRequestTable />
            </section>
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default PartnerRequests;
